import { useEffect } from "react";
import { usePatchPortfolioBankProductMutation } from "web_myplann/Api/Portfolio/Mutations/usePatchPortfolioBankProductMutation";
import type { PortfolioDetailResponseDTO } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { getPortfolioTitle } from "web_myplann/Portfolio/Components/PortfolioSections/portfolioListItemHelpers";
import {
	type BankProductEditFormOutput,
	getBankProductEditFormSchema,
} from "web_myplann/Portfolio/Forms/BankProductForm/bankProductFormSchema";
import { mapPortfolioToBankProductForm } from "web_myplann/Portfolio/Forms/BankProductForm/mapPortfolioToBankProductForm";
import { mapInvestmentEditFormToRequest } from "web_myplann/Portfolio/Forms/InvestmentForm/mapInvestmentFormToRequest";
import { useEditPortfolioForm } from "web_myplann/Portfolio/PortfolioShared/useEditPortfolioForm";

type UseEditBankProductFormArgs = {
	portfolioId: number;
	portfolio: PortfolioDetailResponseDTO;
	detailRoute: string;
};

export const useEditBankProductForm = ({
	portfolioId,
	portfolio,
	detailRoute,
}: UseEditBankProductFormArgs) => {
	const { t } = useResource();
	const { mutate, isPending } =
		usePatchPortfolioBankProductMutation(portfolioId);

	const { form, onSubmit, leaveConfirmation } = useEditPortfolioForm({
		schema: getBankProductEditFormSchema(t),
		defaultValues: mapPortfolioToBankProductForm(portfolio, t),
		mutate: (data: Partial<BankProductEditFormOutput>, options) => {
			mutate(mapInvestmentEditFormToRequest(data, portfolio.tools), options);
		},
		successToast: t(Resources.Portfolio.EditPortfolio.savedToast, {
			type: getPortfolioTitle(portfolio, t),
		}),
		redirectTo: detailRoute,
		isPending,
	});

	useEffect(() => {
		form.trigger();
	}, [form.trigger]);

	return { form, onSubmit, isPending, leaveConfirmation };
};
